"use client";

import { useTranslations } from "next-intl";
import type { UseFormReturn } from "react-hook-form";
import { FormInput, FormSelect } from "@/components/form";
import type { CaloriesCalculatorFormType } from "../schemas/calories-calculator-schema";
import type { ActivityLevel, Goal } from "../types/calories-calculator.type";
import { CaloriesCalculatorGenderField } from "./calories-calculator-gender-field";

const goals: Goal[] = [
  "eat-healthier",
  "maintain",
  "lose-weight",
  "build-muscle",
];

const activityLevels: ActivityLevel[] = ["low", "moderate", "high"];

const triggerClasses =
  "rounded-xl border-border px-4 font-normal text-base text-text hover:border-brand-grey2-400 data-[placeholder]:text-text-subtle data-[state=open]:border-brand-green2-300";

const contentClasses =
  "z-100000001 min-w-(--radix-select-trigger-width) rounded-xl border-border p-2";

const itemClasses =
  "data-[highlighted]:bg-transparent data-[state=checked]:[&>span:first-child]:border-brand-green2-300 data-[state=checked]:[&>span:first-child]:bg-brand-green2-300";

export const CaloriesCalculatorFormStep = ({
  form,
}: {
  form: UseFormReturn<CaloriesCalculatorFormType>;
}) => {
  const t = useTranslations("calories-calculator");

  return (
    <div className="space-y-4">
      <p className="text-lg text-text">{t("description")}</p>

      <CaloriesCalculatorGenderField form={form} />

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-x-2.5">
        <FormSelect
          alwaysDropdown
          classNameContent={contentClasses}
          classNameItem={itemClasses}
          classNameTrigger={triggerClasses}
          clearable={false}
          fieldName="goal"
          fieldOptions={goals.map((goal) => ({
            label: t(`goal.${goal}`),
            value: goal,
          }))}
          fieldPlaceholder={t("goal.title")}
          form={form}
          layout="medium"
        />
        <FormSelect
          alwaysDropdown
          classNameContent={contentClasses}
          classNameItem={itemClasses}
          classNameTrigger={triggerClasses}
          clearable={false}
          fieldName="activityLevel"
          fieldOptions={activityLevels.map((activityLevel) => ({
            label: t(`activity.${activityLevel}`),
            value: activityLevel,
          }))}
          fieldPlaceholder={t("activity.title")}
          form={form}
          layout="medium"
        />
        <FormInput
          fieldLabel={t("height")}
          fieldName="height"
          form={form}
          inputMode="numeric"
        />
        <FormInput
          fieldLabel={t("weight")}
          fieldName="weight"
          form={form}
          inputMode="numeric"
        />
        <FormInput
          fieldLabel={t("age")}
          fieldName="age"
          form={form}
          inputMode="numeric"
        />
      </div>
    </div>
  );
};
